import type { Lot } from './types';
import { partyWallsOf } from './partyWalls';
import { houseStreetsOf, type StreetMap } from './houseStreets';
import { blockKeysOf } from './painted/blockSides';

/** A stretch of a street side, in metres along the street's centreline from its start. */
export interface Span {
  readonly from: number;
  readonly to: number;
}

/** A Lastage block side the woodcut reads: its lots in order along the street, the fronts it draws there, and its stretch. */
export interface ReadSide {
  readonly key: string;
  readonly fronts: number;
  readonly lots: readonly Lot[];
  readonly span: Span;
}

/*
 * The block sides of the Lastage lots as the woodcut readings name them (painted/blockSides.ts),
 * found before any house is taken away: a block ends at a cross way where a corner house stands, so
 * the numbering is read while every corner house still stands. A side's stretch runs from the
 * first front's end to the last's, which the thinning then leaves where it was.
 */
export function readSidesOf(
  lots: readonly Lot[],
  map: StreetMap,
  readings: Readonly<Record<string, { readonly fronts: number }>>
): readonly ReadSide[] {
  const streets = houseStreetsOf(lots, partyWallsOf(lots), map);
  const keys = blockKeysOf(lots, streets);
  const sides = new Map<string, { lot: Lot; along: number }[]>();
  lots.forEach((lot, i) => {
    const along = streets[i]!.along;
    const key = keys[i]!.block;
    if (lot.kind !== 'house' || along === null || !readings[key]) return;
    sides.set(key, [...(sides.get(key) ?? []), { lot, along }]);
  });
  return [...sides].map(([key, row]) => {
    const sorted = [...row].sort((a, b) => a.along - b.along);
    return {
      key,
      fronts: readings[key]!.fronts,
      lots: sorted.map((r) => r.lot),
      span: {
        from: Math.min(...sorted.map((r) => r.along - r.lot.frontage / 2)),
        to: Math.max(...sorted.map((r) => r.along + r.lot.frontage / 2)),
      },
    };
  });
}

/* The free lot whose middle lies nearest a place along the side, counted in lots; the first on a tie. */
function nearestFree(taken: ReadonlySet<number>, count: number, place: number): number {
  let best = -1;
  for (let i = 0; i < count; i++) {
    if (taken.has(i)) continue;
    if (best < 0 || Math.abs(i + 0.5 - place) < Math.abs(best + 0.5 - place)) best = i;
  }
  return best;
}

/**
 * The houses a woodcut-read side keeps: as many as the woodcut draws fronts, first those by the
 * painted buildings, the nearest first, then the rest spread evenly along the side. `lots` are in
 * order along the side; `painted` gives the distance to the painted building each matched lot stands by.
 */
export function frontsOnSide(
  lots: readonly Lot[],
  fronts: number,
  painted: ReadonlyMap<Lot, number>
): readonly Lot[] {
  const count = Math.min(fronts, lots.length);
  const byPainting = lots
    .map((lot, i) => ({ i, distance: painted.get(lot) }))
    .filter((p): p is { i: number; distance: number } => p.distance !== undefined)
    .sort((a, b) => a.distance - b.distance || a.i - b.i)
    .slice(0, count)
    .map((p) => p.i);
  const taken = new Set(byPainting);
  const rest = count - taken.size;
  for (let j = 0; j < rest; j++) taken.add(nearestFree(taken, lots.length, ((j + 0.5) * lots.length) / rest));
  return lots.filter((_, i) => taken.has(i));
}
